import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import StatusBadge from "../../components/StatusBadge";
import { Search as SearchIcon, Plus, Globe, Phone, Lock } from "lucide-react";

const money = (v) =>
    `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const s = (v, fallback = "—") => {
    if (v == null || v === "") return fallback;
    if (typeof v === "string" || typeof v === "number") return v;
    return fallback;
};

const emptyForm = {
    account_name: "",
    industry: "",
    phone: "",
    website: "",
    address: "",
    sap_customer_code: "",
    description: "",
};

export function AccountsList() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [q, setQ] = useState("");
    const [agent, setAgent] = useState("");
    const [agents, setAgents] = useState([]);

    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const load = async (query = "", ag = agent) => {
        setLoading(true);
        setErrored(false);
        try {
            const { data } = await api.get("/crm/accounts", { params: { q: query || undefined, agent: ag || undefined } });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isAdmin) { setLoading(false); return; }
        load();
        (async () => {
            try {
                const { data } = await api.get("/crm/accounts/facets");
                setAgents(data?.agents || []);
            } catch { /* ignore */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const search = (e) => {
        e.preventDefault();
        load(q, agent);
    };

    if (!isAdmin) {
        return (
            <div className="p-3 md:p-6" data-testid="crm-accounts-page">
                <div className="sc-card p-10 text-center">
                    <Lock className="w-6 h-6 mx-auto text-muted-foreground mb-3" />
                    <div className="font-medium">Accounts are restricted to administrators</div>
                    <div className="text-sm text-muted-foreground mt-1">You don't have access to the Accounts directory.</div>
                </div>
            </div>
        );
    }

    const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const resetForm = () => {
        setForm(emptyForm);
        setShowCreate(false);
    };

    const createAccount = async () => {
        if (!form.account_name.trim()) {
            toast.error("Account name is required.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/crm/accounts", form);
            toast.success("Account created");
            resetForm();
            await load(q);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-accounts-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">CRM</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Accounts</h1>
                </div>
                <button onClick={() => setShowCreate(true)} className="h-9 inline-flex items-center gap-1 px-4 rounded bg-foreground text-background text-sm" data-testid="account-add-button">
                    <Plus className="w-4 h-4" /> New Account
                </button>
            </div>

            <form onSubmit={search} className="sc-card p-3 flex flex-col md:flex-row gap-3">
                <select
                    className="sc-input md:w-56"
                    value={agent}
                    onChange={(e) => { setAgent(e.target.value); load(q, e.target.value); }}
                    data-testid="account-filter-agent"
                >
                    <option value="">All owners</option>
                    {agents.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <div className="relative flex-1">
                    <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input className="sc-input pl-9" placeholder="Search account name…" value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
                <button type="submit" className="h-9 px-4 rounded border border-border text-sm hover:bg-secondary">Search</button>
            </form>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[900px]">
                        <thead>
                            <tr>
                                <th>Account</th>
                                <th>Owner (Agent)</th>
                                <th>Industry</th>
                                <th>Phone</th>
                                <th>SAP Customer Code</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
                            {!loading && errored && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Couldn't load accounts.</td></tr>}
                            {!loading && !errored && rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No accounts found.</td></tr>}
                            {!loading && !errored && rows.map((a) => (
                                <tr key={a.id} className="cursor-pointer" onClick={() => window.location.assign(`/crm/accounts/${a.id}`)} data-testid={`account-row-${a.id}`}>
                                    <td className="font-medium">{s(a.account_name)}</td>
                                    <td className="text-sm">{s(a.owner_name, "(unassigned)")}</td>
                                    <td className="text-muted-foreground">{s(a.industry)}</td>
                                    <td className="font-mono text-xs">{s(a.phone)}</td>
                                    <td className="font-mono text-xs">{s(a.sap_customer_code)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showCreate && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={resetForm}>
                    <div className="sc-card p-5 w-full max-w-xl space-y-4 bg-background" onClick={(e) => e.stopPropagation()} data-testid="account-create-modal">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Create Account</div>
                            <div className="text-lg font-semibold">New Account</div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Account Name" required full><input className="sc-input" value={form.account_name} onChange={(e) => updateForm("account_name", e.target.value)} /></Field>
                            <Field label="Industry"><input className="sc-input" value={form.industry} onChange={(e) => updateForm("industry", e.target.value)} /></Field>
                            <Field label="Phone"><input className="sc-input" value={form.phone} onChange={(e) => updateForm("phone", e.target.value)} /></Field>
                            <Field label="Website"><input className="sc-input" value={form.website} onChange={(e) => updateForm("website", e.target.value)} /></Field>
                            <Field label="SAP Customer Code"><input className="sc-input" value={form.sap_customer_code} onChange={(e) => updateForm("sap_customer_code", e.target.value)} /></Field>
                            <Field label="Address" full><input className="sc-input" value={form.address} onChange={(e) => updateForm("address", e.target.value)} /></Field>
                            <Field label="Description" full><textarea className="sc-input h-auto py-2" rows={3} value={form.description} onChange={(e) => updateForm("description", e.target.value)} /></Field>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={resetForm} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={createAccount} disabled={saving} className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50" data-testid="account-save">
                                {saving ? "Creating…" : "Create Account"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function AccountDetail() {
    const { id } = useParams();
    const [account, setAccount] = useState(null);
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/crm/accounts/${id}`);
                setAccount(data && typeof data === "object" ? data : null);
            } catch (e) {
                setErrored(true);
                toast.error(formatApiError(e));
            }
        })();
    }, [id]);

    if (errored) {
        return <div className="p-3 md:p-6"><div className="sc-card p-8 text-center text-sm text-muted-foreground">Couldn't load this account.</div></div>;
    }
    if (!account) {
        return <div className="p-3 md:p-6"><div className="sc-card p-8 text-center text-sm text-muted-foreground">Loading…</div></div>;
    }

    const contacts = Array.isArray(account.contacts) ? account.contacts : [];
    const deals = Array.isArray(account.deals) ? account.deals : [];

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-account-detail-page">
            <div>
                <div className="sc-section-title">Account</div>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight">{s(account.account_name)}</h1>
                <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-3">
                    {account.industry && <span>{s(account.industry)}</span>}
                    {account.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{s(account.phone)}</span>}
                    {account.website && <span className="inline-flex items-center gap-1"><Globe className="w-3.5 h-3.5" />{s(account.website)}</span>}
                </div>
            </div>

            <div className="sc-card">
                <div className="px-4 py-3 border-b border-border"><h2 className="text-sm font-medium">Contacts</h2></div>
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[700px]">
                        <thead><tr><th>Name</th><th>Title</th><th>Email</th><th>Phone</th></tr></thead>
                        <tbody>
                            {contacts.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No contacts yet.</td></tr>}
                            {contacts.map((c) => (
                                <tr key={c.id}>
                                    <td>{`${s(c.first_name, "")} ${s(c.last_name, "")}`.trim() || "—"}</td>
                                    <td className="text-muted-foreground">{s(c.title)}</td>
                                    <td className="text-xs">{s(c.email)}</td>
                                    <td className="font-mono text-xs">{s(c.phone || c.mobile)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="sc-card">
                <div className="px-4 py-3 border-b border-border"><h2 className="text-sm font-medium">Deals</h2></div>
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[800px]">
                        <thead><tr><th>Deal</th><th>Stage</th><th className="text-right">Amount</th><th>Closing</th></tr></thead>
                        <tbody>
                            {deals.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No deals yet.</td></tr>}
                            {deals.map((d) => (
                                <tr key={d.id} className="cursor-pointer" onClick={() => window.location.assign(`/crm/deals/${d.id}`)}>
                                    <td className="font-medium">{s(d.deal_name)}</td>
                                    <td><StatusBadge status={s(d.stage, "open")} /></td>
                                    <td className="sc-num">{money(d.amount)}</td>
                                    <td className="text-muted-foreground text-xs">{d.closing_date ? new Date(d.closing_date).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" }) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Link to="/crm/accounts" className="text-sm text-muted-foreground hover:underline">&larr; Back to Accounts</Link>
        </div>
    );
}

function Field({ label, required = false, full = false, children }) {
    return (
        <label className={`space-y-1 block ${full ? "md:col-span-2" : ""}`}>
            <div className="text-xs font-medium">{label}{required && <span className="text-red-500 ml-1">*</span>}</div>
            {children}
        </label>
    );
}
